import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUserMd, FaPlus, FaEdit, FaTrash, FaSpinner, FaUsers, FaTimes, FaEye, FaFileAlt, FaShieldAlt, FaStethoscope } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/adminAPI';

const CoachManagement = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    phone: '',
    specialization: '',
    bio: '',
  });

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const data = await adminAPI.getAllCoaches();
      setCoaches(data);
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
      toast.error('Failed to load coaches');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      specialization: '',
      bio: '',
    });
  };

  const openCreateModal = () => {
    resetForm();
    setModalMode('create');
    setSelectedCoach(null);
    setShowModal(true);
  };

  const openEditModal = (coach) => {
    setFormData({
      firstName: coach.firstName || '',
      lastName: coach.lastName || '',
      email: coach.email || '',
      password: '',
      phone: coach.phone || '',
      specialization: coach.specialization || '',
      bio: coach.bio || '',
    });
    setModalMode('edit');
    setSelectedCoach(coach);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (modalMode === 'create') {
        const newCoach = await adminAPI.createCoach(formData);
        setCoaches([...coaches, newCoach]);
        toast.success('Coach created successfully');
      } else {
        const { email, password, ...updateData } = formData;
        const updatedCoach = await adminAPI.updateCoach(selectedCoach.id, updateData);
        setCoaches(coaches.map(c => c.id === selectedCoach.id ? updatedCoach : c));
        toast.success('Coach updated successfully');
      }
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Submit failed:', error);
      toast.error(error.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (coachId) => {
    if (!window.confirm('Are you sure you want to delete this coach? All clients will be unassigned.')) {
      return;
    }

    setDeleting(coachId);
    try {
      await adminAPI.deleteCoach(coachId);
      setCoaches(coaches.filter(c => c.id !== coachId));
      toast.success('Coach deleted successfully');
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete coach');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <FaShieldAlt className="text-2xl text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500">Manage coaches, clients, and documents</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 bg-white rounded-xl p-2 shadow-lg">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaFileAlt />
            Documents
          </Link>
          <Link
            to="/admin/coaches"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-primary-600 text-white"
          >
            <FaUserMd />
            Coaches
          </Link>
          <Link
            to="/admin/clients"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaUsers />
            Clients
          </Link>
          <Link
            to="/admin/doctors"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaStethoscope />
            Doctors
          </Link>
        </div>

        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Coach Management</h2>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            <FaPlus />
            Add Coach
          </button>
        </div>

        {/* Coaches Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <FaSpinner className="animate-spin text-3xl text-gray-400" />
          </div>
        ) : coaches.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <FaUserMd className="text-5xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Coaches Yet</h3>
            <p className="text-gray-500 mb-6">Add your first coach to get started</p>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              <FaPlus />
              Add Coach
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coaches.map((coach) => (
              <div
                key={coach.id}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {coach.firstName?.charAt(0)}{coach.lastName?.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {coach.firstName} {coach.lastName}
                      </h3>
                      <p className="text-sm text-gray-500">{coach.email}</p>
                    </div>
                  </div>
                </div>

                {coach.specialization && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {coach.specialization}
                    </span>
                  </div>
                )}

                {coach.bio && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{coach.bio}</p>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm">
                    <FaUsers className="text-primary-500" />
                    <span className="font-medium text-gray-700">{coach.clientCount || 0} Clients</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditModal(coach)}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => handleDelete(coach.id)}
                      disabled={deleting === coach.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {deleting === coach.id ? <FaSpinner className="animate-spin" /> : <FaTrash />}
                    </button>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-400">
                  Added {formatDate(coach.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">
                  {modalMode === 'create' ? 'Add New Coach' : 'Edit Coach'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {modalMode === 'create' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                    <strong>Note:</strong> The coach must first register an account at <strong>/register</strong> (selecting role "Coach" if available, or as a regular user). Enter their registered email below to assign them the Coach role.
                  </div>
                )}

                {modalMode === 'create' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Coach's Registered Email *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="coach@example.com"
                    />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Specialization
                  </label>
                  <input
                    type="text"
                    value={formData.specialization}
                    onChange={(e) => setFormData({ ...formData, specialization: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Weight Loss, Sports Nutrition"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bio
                  </label>
                  <textarea
                    rows={3}
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Brief description about the coach..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                  >
                    {submitting ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Saving...
                      </>
                    ) : modalMode === 'create' ? (
                      'Create Coach'
                    ) : (
                      'Update Coach'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CoachManagement;
